import { useTranslations } from '@fshr/context/i18n-context';
import BevSafeText from '../bev-safe-text';
import { PricingComponent } from './pricing-component';
import { OfferCtaComponent } from './offer-cta-component';
import { OfferComponent } from './offer-component';
import HorizontalLine from '../horizontal-line';
import { useMediaQuery } from 'react-responsive';
import { cn } from '@fshr/shared-react';
import { RateCardProps } from './rate-card-dialog';

const DialogItemListSection = ({
	itemList,
	title,
	asHtmlText,
}: {
	title: string;
	itemList?: string[];
	asHtmlText?: boolean;
}) => {
	const translate = useTranslations();
	if (!itemList || itemList.length === 0) {
		return null;
	}
	return (
		<div className="flex flex-col spacing-stack-xs">
			<h3 className="text-label">
				<span className="text-text-primary">{translate(title)}</span>
			</h3>
			<ul className="text-body3 list-disc list-outside space-y-1 ml-[1.5em]">
				{itemList.map((inclusion, i) => {
					if (asHtmlText) {
						return (
							<li
								className="text-text-primary"
								key={i}
								dangerouslySetInnerHTML={{ __html: inclusion }}
							></li>
						);
					}
					return (
						<li className="text-text-primary" key={i}>
							{inclusion}
						</li>
					);
				})}
			</ul>
		</div>
	);
};

const DialogInnerHtmlSection = ({ htmlText, title }: { title: string; htmlText?: string }) => {
	const translate = useTranslations();

	return (
		<div className="flex flex-col spacing-stack-xs">
			<span className="text-label">{translate(title)}</span>
			{htmlText ? (
				<div
					className="flex flex-col text-body3 gap-[20px] lg:gap-[30px]"
					dangerouslySetInnerHTML={{ __html: htmlText }}
				></div>
			) : null}
		</div>
	);
};

export const MainRateCardDialogComponent = ({
	offerData,
	locale,
	isCartEnabled,
	isMultiRoom,
	mainContainerClassName,
	isPreferredPartner,
	handleAddToCart,
	handleSelectOrBookNow,
	handleBedOptionsSelect,
	allInPricing,
	isInRateDialog,
	ppMode,
}: RateCardProps) => {
	const translate = useTranslations();
	const hasBedOptions = (offerData?.beds?.length ?? 0) > 1;
	const isDesktop = useMediaQuery({ query: '(min-width: 960px)' });

	return (
		<div
			className={cn(
				'h-full flex flex-col items-start justify-start spacing-stack-l w-full',
				mainContainerClassName
			)}
		>
			{offerData?.title || isPreferredPartner ? (
				<div className="w-full flex flex-col spacing-stack-m">
					<div className="flex flex-col spacing-stack-2xs">
						{isPreferredPartner ? (
							<p className="text-label">
								<span className="text-eci-lime-green-800">{translate('Preferred Partner')}</span>
							</p>
						) : null}
						{offerData?.title ? (
							<div role="heading" aria-level={1} className="text-subtitle1">
								<span className="text-text-heading">
									<BevSafeText>{offerData.title}</BevSafeText>
								</span>
							</div>
						) : null}
					</div>
					<HorizontalLine additionalClasses="border-border-subtle" />
				</div>
			) : null}
			<div className="flex flex-col flex-1 h-full spacing-stack-l w-full">
				<div
					className={cn('flex flex-col lg:flex-row lg:justify-between items-start', {
						'spacing-inline-s': isDesktop,
						'spacing-stack-l': !isDesktop,
					})}
				>
					<div
						className={cn('flex flex-col', {
							'spacing-stack-s': isDesktop,
							'spacing-stack-l': !isDesktop,
						})}
					>
						{offerData ? (
							<PricingComponent
								mainContainerClassName="flex flex-col w-full spacing-stack-2xs items-start"
								priceLabelContainerClassName="flex flex-row gap-x-inline-2xs items-start"
								offerPriceClassName="text-caption font-bold"
								offerEstimatedTotalClassName="text-caption font-bold"
								feesDisclaimerContainerClassName="text-caption text-text-primary font-bold lg:max-w-[375px]"
								offer={offerData}
								locale={locale}
								allInPricing={allInPricing}
							/>
						) : null}
						{offerData ? (
							<OfferComponent
								offer={offerData}
								isInDialog={true}
								mainContainerClassName="flex flex-col gap-[6px]!"
								isPreferredPartner={isPreferredPartner}
								isInRateDialog={isInRateDialog}
							/>
						) : null}
					</div>
					{!handleBedOptionsSelect && !handleAddToCart && !handleSelectOrBookNow ? null : (
						<div className="w-full lg:w-auto pr-[10px]">
							<OfferCtaComponent
								ctaContainerClassName="w-full lg:w-auto"
								isInDialog={true}
								hasBedOptions={Boolean(hasBedOptions)}
								isCartEnabled={isCartEnabled}
								isMultiRoom={isMultiRoom}
								handleAddToCart={handleAddToCart}
								handleSelectOrBookNow={handleSelectOrBookNow}
								handleBedOptionsSelect={handleBedOptionsSelect}
								ppMode={ppMode}
							/>
						</div>
					)}
				</div>
				<HorizontalLine additionalClasses="border-border-subtle" />
				<div className="flex flex-col spacing-stack-l pb-400">
					{offerData?.shortDescription ? (
						<p className="text-body3">
							<span className="text-text-primary">
								<BevSafeText>{offerData?.shortDescription}</BevSafeText>
							</span>
						</p>
					) : null}
					{offerData?.inclusions?.map(incl => (
						<DialogItemListSection itemList={incl.data} title={incl.title || 'Included'} />
					))}
					{isPreferredPartner
						? offerData?.ppAmenities?.map(ppAm => (
								<DialogItemListSection
									itemList={ppAm.data}
									title={ppAm.title || 'Exclusive Preferred Partner Benefits'}
									asHtmlText
								/>
							))
						: null}
					{offerData?.conditions?.map(cond => (
						<DialogItemListSection itemList={cond.data} title={cond.title || 'Conditions'} />
					))}
					{offerData?.cancellationPolicy && (
						<DialogInnerHtmlSection
							htmlText={offerData?.cancellationPolicy}
							title={translate('Cancellation policy')}
						/>
					)}
					{offerData?.disclaimer && (
						<DialogInnerHtmlSection
							htmlText={offerData?.disclaimer}
							title={translate('Terms & conditions')}
						/>
					)}
				</div>
			</div>
		</div>
	);
};
